import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtGHS, fmtNum, fmtDateTime } from "@/lib/format";
import { Clock, AlertTriangle, LockKeyhole, FileText } from "lucide-react";
import { toast } from "sonner";
import { ShiftSummary } from "@/components/ShiftSummary";

export default function ShiftOversight() {
  const { branches } = useBranch();
  const [branchId, setBranchId] = useState("all");
  const [open, setOpen] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [closeFor, setCloseFor] = useState(null);
  const [counted, setCounted] = useState("");
  const [note, setNote] = useState("");
  const [summaryFor, setSummaryFor] = useState(null);
  const [closed, setClosed] = useState([]);

  const load = useCallback(() => {
    const params = branchId !== "all" ? { branch_id: branchId } : {};
    api.get("/shifts", { params: { ...params, status: "open" } }).then((r) => setOpen(r.data)).catch(() => {});
    api.get("/shifts", { params: { ...params, flagged: true } }).then((r) => setFlagged(r.data)).catch(() => {});
    api.get("/shifts", { params: { ...params, status: "closed" } }).then((r) => setClosed(r.data.slice(0, 20))).catch(() => {});
  }, [branchId]);
  useEffect(() => { load(); }, [load]);

  const expected = closeFor ? Number(closeFor.opening_float || 0) + Number(closeFor.totals?.cash || 0) : 0;
  const variance = (Number(counted) || 0) - expected;

  const doForceClose = async () => {
    try {
      const { data } = await api.post(`/shifts/${closeFor.id}/force-close`, { counted_cash: Number(counted) || 0, note });
      toast.success(`${data.code} force-closed — variance ${fmtGHS(data.variance)}`);
      setCloseFor(null); setCounted(""); setNote(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Shift Oversight" subtitle="Monitor open cashier shifts and review flagged cash variances." />

      <div className="mb-4 w-56">
        <Label className="text-xs">Branch</Label>
        <Select value={branchId} onValueChange={setBranchId}>
          <SelectTrigger data-testid="oversight-branch-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All branches</SelectItem>
            {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><Clock className="h-4 w-4" /> Open shifts now</h3>
      <Card className="shadow-sm overflow-x-auto mb-8">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr><th className="py-2.5 px-3">Shift</th><th className="py-2.5 px-3">Branch</th><th className="py-2.5 px-3">Cashier</th><th className="py-2.5 px-3">Opened</th><th className="py-2.5 px-3 text-right">Sales</th><th className="py-2.5 px-3 text-right">Cash</th><th className="py-2.5 px-3 text-right">Float</th><th className="py-2.5 px-3"></th></tr>
          </thead>
          <tbody>
            {open.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-muted-foreground"><Clock className="h-8 w-8 mx-auto mb-2 opacity-40" />No open shifts</td></tr>}
            {open.map((s) => (
              <tr key={s.id} className="border-t border-border" data-testid={`open-shift-${s.code}`}>
                <td className="py-2 px-3"><StatusBadge value="ok" label={s.shift_label} /> <span className="font-mono text-xs text-muted-foreground ml-1">{s.code}</span></td>
                <td className="py-2 px-3">{s.branch_name}</td>
                <td className="py-2 px-3">{s.cashier_name}</td>
                <td className="py-2 px-3 text-muted-foreground text-xs">{fmtDateTime(s.opened_at)}</td>
                <td className="py-2 px-3 text-right font-mono">{fmtNum(s.totals?.sales_count || 0)}</td>
                <td className="py-2 px-3 text-right font-mono">{fmtGHS(s.totals?.cash || 0)}</td>
                <td className="py-2 px-3 text-right font-mono">{fmtGHS(s.opening_float || 0)}</td>
                <td className="py-2 px-3 text-right"><Button size="sm" variant="outline" className="text-rose-600 border-rose-300" data-testid={`force-close-${s.code}`} onClick={() => { setCloseFor(s); setCounted(""); setNote(""); }}><LockKeyhole className="h-3.5 w-3.5 mr-1" /> Force close</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-rose-600" /> Flagged cash variances <span className="text-xs font-normal text-muted-foreground">(exceeded the threshold in Business Rules)</span></h3>
      <Card className="shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr><th className="py-2.5 px-3">Shift</th><th className="py-2.5 px-3">Branch</th><th className="py-2.5 px-3">Cashier</th><th className="py-2.5 px-3">Closed</th><th className="py-2.5 px-3 text-right">Expected</th><th className="py-2.5 px-3 text-right">Counted</th><th className="py-2.5 px-3 text-right">Variance</th><th className="py-2.5 px-3"></th></tr>
          </thead>
          <tbody>
            {flagged.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-muted-foreground"><AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-40" />No flagged variances</td></tr>}
            {flagged.map((s) => (
              <tr key={s.id} className="border-t border-border" data-testid={`flagged-shift-${s.code}`}>
                <td className="py-2 px-3">{s.shift_label} <span className="font-mono text-xs text-muted-foreground ml-1">{s.code}</span></td>
                <td className="py-2 px-3">{s.branch_name}</td>
                <td className="py-2 px-3">{s.cashier_name}</td>
                <td className="py-2 px-3 text-muted-foreground text-xs">{fmtDateTime(s.closed_at)}</td>
                <td className="py-2 px-3 text-right font-mono">{fmtGHS(s.expected_cash)}</td>
                <td className="py-2 px-3 text-right font-mono">{fmtGHS(s.counted_cash)}</td>
                <td className="py-2 px-3 text-right font-mono text-rose-600 font-medium">{fmtGHS(s.variance)}</td>
                <td className="py-2 px-3 text-right"><Button size="sm" variant="outline" onClick={() => setSummaryFor(s)} data-testid={`shift-summary-${s.code}`}><FileText className="h-3.5 w-3.5 mr-1" /> Summary</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <h3 className="text-sm font-semibold mb-2 mt-8 flex items-center gap-2"><FileText className="h-4 w-4" /> Recently closed shifts</h3>
      <Card className="shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr><th className="py-2.5 px-3">Shift</th><th className="py-2.5 px-3">Branch</th><th className="py-2.5 px-3">Cashier</th><th className="py-2.5 px-3">Closed</th><th className="py-2.5 px-3 text-right">Orders</th><th className="py-2.5 px-3 text-right">Gross</th><th className="py-2.5 px-3 text-right">Variance</th><th className="py-2.5 px-3"></th></tr>
          </thead>
          <tbody>
            {closed.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">No closed shifts yet</td></tr>}
            {closed.map((s) => (
              <tr key={s.id} className="border-t border-border" data-testid={`closed-shift-${s.code}`}>
                <td className="py-2 px-3">{s.shift_label} <span className="font-mono text-xs text-muted-foreground ml-1">{s.code}</span></td>
                <td className="py-2 px-3">{s.branch_name}</td>
                <td className="py-2 px-3">{s.cashier_name}</td>
                <td className="py-2 px-3 text-muted-foreground text-xs">{fmtDateTime(s.closed_at)}</td>
                <td className="py-2 px-3 text-right font-mono">{fmtNum(s.totals?.sales_count || 0)}</td>
                <td className="py-2 px-3 text-right font-mono">{fmtGHS(s.totals?.gross || 0)}</td>
                <td className={`py-2 px-3 text-right font-mono ${s.variance ? "text-rose-600" : ""}`}>{fmtGHS(s.variance || 0)}</td>
                <td className="py-2 px-3 text-right"><Button size="sm" variant="outline" onClick={() => setSummaryFor(s)} data-testid={`closed-shift-summary-${s.code}`}><FileText className="h-3.5 w-3.5 mr-1" /> Summary</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!summaryFor} onOpenChange={(o) => !o && setSummaryFor(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Shift summary</DialogTitle></DialogHeader>
          {summaryFor && <ShiftSummary shift={summaryFor} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!closeFor} onOpenChange={(o) => !o && setCloseFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Force close {closeFor?.code}</DialogTitle></DialogHeader>
          {closeFor && (
            <div className="space-y-3 text-sm">
              <div className="text-muted-foreground">{closeFor.shift_label} · {closeFor.cashier_name} · {closeFor.branch_name}</div>
              <div className="rounded-lg border border-border divide-y divide-border px-1">
                <div className="flex justify-between py-1.5"><span className="text-muted-foreground">Cash sales</span><span className="font-mono">{fmtGHS(closeFor.totals?.cash || 0)}</span></div>
                <div className="flex justify-between py-1.5"><span className="text-muted-foreground">Opening float</span><span className="font-mono">{fmtGHS(closeFor.opening_float || 0)}</span></div>
                <div className="flex justify-between py-1.5 font-medium"><span>Expected cash</span><span className="font-mono">{fmtGHS(expected)}</span></div>
              </div>
              <div><Label>Counted cash (GHS)</Label><Input type="number" data-testid="oversight-counted-cash" value={counted} onChange={(e) => setCounted(e.target.value)} /></div>
              <div className={`flex justify-between font-medium ${variance === 0 ? "text-emerald-600" : "text-rose-600"}`}><span>Variance</span><span className="font-mono">{fmtGHS(variance)}</span></div>
              <div><Label>Reason / note</Label><Input data-testid="oversight-note" value={note} onChange={(e) => setNote(e.target.value)} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setCloseFor(null)}>Cancel</Button><Button className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-force-close-btn" onClick={doForceClose}>Force close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
